import { Box, Button, Stack } from '@mui/material';
import { useLayoutEffect } from 'react';
import { Chart } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import moment from 'moment';
import { DATE_FORMAT } from '../../../utils/constants';
import { getMomentDate, getPartialDataLabel } from '../../../utils/getDate';
import { partialDataBackground } from '../../../utils/chartConfigs';

export default function UsersTrend({ trendsData = {}, handleExpand, viewOption }) {
  const keys = Object.keys(trendsData);
  const values = Object.values(trendsData);

  const partialLabel = getPartialDataLabel(keys, viewOption);
  const transformedLabels = keys.map((key) => moment(key).format(viewOption === 'month' ? 'MMM YYYY' : 'DD MMM YYYY'));
  const backgroundColor = partialDataBackground(keys, partialLabel);

  const chartData = {
    labels: transformedLabels,
    datasets: [
      {
        type: 'line',
        label: 'Users',
        data: Object.values(trendsData),
        fill: false,
        borderWidth: 1,
        borderColor: backgroundColor,
        pointStyle: 'circle',
        pointBackgroundColor: backgroundColor,
        pointRadius: 1,
      },
      {
        type: 'bar',
        label: 'Users (Bar)',
        data: values,
        backgroundColor,
        borderColor: backgroundColor,
        borderWidth: 1,
      },
    ],
  };

  const options = {
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: false,
        mode: 'index',
        external(context) {
          let tooltipEl = document.getElementById('chartjs-tooltip');
          if (!tooltipEl) {
            tooltipEl = document.createElement('div');
            tooltipEl.id = 'chartjs-tooltip';
            tooltipEl.innerHTML = '<table></table>';
            document.body.appendChild(tooltipEl);
          }

          const tooltipModel = context.tooltip;
          if (tooltipModel.opacity === 0) {
            tooltipEl.style.opacity = '0';
            return;
          }

          const { chart, tooltip } = context;
          const dataIndex = tooltip.dataPoints?.[0]?.dataIndex ?? -1;
          const dynamicColor = chart.data.datasets?.[1]?.backgroundColor?.[dataIndex] || '#4482FF';

          tooltipEl.classList.remove('below', 'no-transform');
          const { $context = {} } = tooltipModel;
          const { tooltipItems = [] } = $context;
          if (tooltipItems && tooltipItems.length) {
            const dateObj = tooltipItems?.[0]?.label ? getMomentDate(tooltipItems?.[0]?.label) : '';
            const label = dateObj.format(viewOption === 'month' ? DATE_FORMAT.MONTH_DATE_GRAPH : DATE_FORMAT.DAY_DATE_GRAPH);
            const actual = tooltipItems?.[0]?.raw;
            const innerHTML = `
              <div style="width: 150px; background-color: rgba(0, 0, 0, 0.7); border-radius: 5px; box-shadow: 0 0 5px rgba(0, 0, 0, 0.5); padding: 3px;">
                  <div style="padding: 3px; background-color: transparent; border-top-left-radius: 5px; border-top-right-radius: 5px;">
                      <span style="color: white; font-size: 15px;">${label}</span>
                  </div>
                  <hr style="margin: 0; border-color: white;">
                  <div style="padding: 3px;">
                  <p style="color: white; margin-left: 4px; font-size: 14px; display: flex; align-items: center; gap: 5px">
                  <span style="display: inline-block; width: 10px; height: 10px; border-radius: 50%; background-color: ${dynamicColor};"></span>
                  Users : ${actual}
                </p>
                  </div>
              </div>`;

            tooltipEl.querySelector('table').innerHTML = innerHTML;
          }

          const position = context.chart.canvas.getBoundingClientRect();

          tooltipEl.style.opacity = '1';
          tooltipEl.style.position = 'absolute';

          const tooltipWidth = tooltipEl.offsetWidth;
          let left = position.left + window.pageXOffset + tooltipModel.caretX;
          if (left + tooltipWidth > position.right) {
            left -= (left + tooltipWidth - position.right);
          }

          tooltipEl.style.left = `${left}px`;

          tooltipEl.style.top = `${position.top + window.pageYOffset + tooltipModel.caretY}px`;
          tooltipEl.style.padding = `${tooltipModel.padding}px ${tooltipModel.padding}px`;
          tooltipEl.style.pointerEvents = 'none';
        },
      },
    },
    scales: {
      x: {
        display: false,
      },
      y: {
        ticks: { precision: 0 },
        display: false,
        suggestedMin: 0,
        suggestedMax: 100,
      },
    },
  };

  useLayoutEffect(() => {
    ChartJS.register(
      CategoryScale,
      LinearScale,
      BarElement,
      LineElement,
      PointElement,
      ArcElement,
      Title,
      Tooltip,
      Legend,
      Filler,
    );

    return () => {
      ChartJS.unregister(
        CategoryScale,
        LinearScale,
        BarElement,
        LineElement,
        PointElement,
        ArcElement,
        Title,
        Tooltip,
        Legend,
        Filler,
      );
    };
  }, [trendsData, chartData, options]);

  if (trendsData && Object.keys(trendsData).length) {
    return (
      <Stack direction="row" alignItems="center" justifyContent="center">
        <Box sx={{ width: 84, height: 40 }}>
          <Chart type="bar" data={chartData} options={options} />
        </Box>
        <Button
          variant="outlined"
          size="small"
          sx={{
            minHeight: '18px',
            padding: '4px',
            fontSize: '0.8rem',
            lineHeight: 1,
          }}
          onClick={handleExpand}
        >
          Expand
        </Button>

      </Stack>
    );
  }
}
